import { API } from '@/lib/api';
import { useState, useEffect, useRef, useCallback } from "react";
import { getErrorMessage } from "@/lib/utils";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Navigation, Phone, MessageCircle, Star, Clock, ArrowLeft, CheckCircle2, Truck, Package, Car, Home, Briefcase, Wrench, Activity, AlertTriangle, Bell, ThumbsUp, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Navbar from "@/components/layout/Navbar";
import ReviewModal from "@/components/ReviewModal";
import axios from "axios";
import Map, { Marker, Source, Layer, NavigationControl } from "react-map-gl";


const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

const SERVICE_ICONS = {
  cargo_delivery: Truck, courier: Package, towing: Car,
  movers: Home, laborer: Briefcase, roadside_assistance: Wrench,
};
const SERVICE_LABELS = {
  cargo_delivery: "Cargo Delivery", courier: "Courier", towing: "Towing",
  movers: "Movers", laborer: "Laborer", roadside_assistance: "Roadside Assistance",
};

const TRIP_STEPS = [
  { key: "en_route", label: "On the way" },
  { key: "arriving", label: "Arriving" },
  { key: "arrived", label: "Arrived" },
];

const playArrivalSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    osc.frequency.setValueAtTime(1100, ctx.currentTime + 0.15);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.6);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.6);
  } catch {}
  try { navigator.vibrate?.([200, 100, 200, 100, 300]); } catch {}
};

const LiveTrackingPage = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewState, setViewState] = useState({ latitude: 43.65, longitude: -79.38, zoom: 13 });
  const [arrivedNotified, setArrivedNotified] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [completedNotified, setCompletedNotified] = useState(false);
  const pollRef = useRef(null);
  const mapRef = useRef(null);
  const prevTripStatus = useRef(null);

  const fetchTracking = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/service-requests/${requestId}/tracking`, { withCredentials: true });
      setTracking(res.data);
      setError(null);

      // Trip status change detection
      const newStatus = res.data.trip_status;
      if (newStatus && newStatus !== prevTripStatus.current) {
        if (newStatus === "arrived" && !arrivedNotified) {
          playArrivalSound();
          setArrivedNotified(true);
        }
        if (newStatus === "completed" && !completedNotified) {
          setCompletedNotified(true);
          setShowReview(true);
        }
        prevTripStatus.current = newStatus;
      }

      // Auto-fit map bounds to show both professional and pickup
      const pCoords = res.data.professional_location?.coordinates;
      const pPickup = res.data.request?.pickup_coords;
      if (pCoords?.length === 2 && pPickup?.latitude && mapRef.current) {
        try {
          const map = mapRef.current.getMap();
          const bounds = [
            [Math.min(pCoords[0], pPickup.longitude), Math.min(pCoords[1], pPickup.latitude)],
            [Math.max(pCoords[0], pPickup.longitude), Math.max(pCoords[1], pPickup.latitude)],
          ];
          map.fitBounds(bounds, { padding: 80, maxZoom: 15, duration: 1000 });
        } catch {}
      }

      return res.data;
    } catch (err) {
      setError(getErrorMessage(err, "Failed to load tracking data"));
      return null;
    } finally {
      setLoading(false);
    }
  }, [requestId, arrivedNotified]);

  useEffect(() => {
    fetchTracking().then((data) => {
      if (!data) return;
      const proCoords = data.professional_location?.coordinates;
      const pickup = data.request?.pickup_coords;
      if (proCoords?.length === 2) {
        setViewState((v) => ({ ...v, latitude: proCoords[1], longitude: proCoords[0], zoom: 14 }));
      } else if (pickup?.latitude) {
        setViewState((v) => ({ ...v, latitude: pickup.latitude, longitude: pickup.longitude, zoom: 14 }));
      }
    });
  }, [requestId]);

  useEffect(() => {
    pollRef.current = setInterval(fetchTracking, 5000);
    return () => clearInterval(pollRef.current);
  }, [fetchTracking]);

  useEffect(() => {
    if (tracking?.request?.status === "completed" || tracking?.trip_status === "completed") clearInterval(pollRef.current);
  }, [tracking?.request?.status, tracking?.trip_status]);

  const proCoords = tracking?.professional_location?.coordinates;
  const pickup = tracking?.request?.pickup_coords;
  const dropoff = tracking?.request?.dropoff_coords;
  const routeGeo = tracking?.route;
  const Icon = SERVICE_ICONS[tracking?.request?.service_type] || Package;
  const tripStatus = tracking?.trip_status || "en_route";
  const proName = tracking?.professional?.name || "Professional";
  const firstName = tracking?.professional?.first_name || proName.split(" ")[0];
  const statusMessage = tracking?.status_message || `${firstName} is on the way`;
  const isCompleted = tracking?.request?.status === "completed" || tripStatus === "completed";
  const currentStepIdx = TRIP_STEPS.findIndex(s => s.key === (isCompleted ? "arrived" : tripStatus));

  const statusColors = {
    en_route: "bg-emerald-600",
    arriving: "bg-amber-500",
    arrived: "bg-blue-600",
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Navbar />
        <div className="flex items-center justify-center pt-40">
          <Activity className="w-8 h-8 animate-pulse text-emerald-600" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Navbar />
        <div className="pt-32 px-4 max-w-lg mx-auto text-center">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-stone-800 mb-2">Tracking Unavailable</h2>
          <p className="text-stone-500 mb-6">{error}</p>
          <Button onClick={() => navigate("/my-bookings")} variant="outline" data-testid="back-to-bookings">Back to Bookings</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="live-tracking-page">
      <Navbar />
      <div className="pt-20">
        {/* Status Banner */}
        <div className={`sticky top-20 z-50 transition-colors duration-500 ${isCompleted ? "bg-stone-600" : statusColors[tripStatus] || "bg-emerald-600"}`} data-testid="tracking-status-bar">
          <div className="max-w-5xl mx-auto px-4 py-2.5 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isCompleted ? <CheckCircle2 className="w-4 h-4 text-white" /> :
               tripStatus === "arrived" ? <Bell className="w-4 h-4 text-white animate-bounce" /> :
               <Navigation className="w-4 h-4 text-white animate-pulse" />}
              <span className="text-white text-sm font-medium" data-testid="status-message">
                {isCompleted ? "Service Completed" : statusMessage}
              </span>
            </div>
            {tracking?.eta && !isCompleted && tripStatus === "en_route" && (
              <Badge className="bg-white/20 text-white border-white/30" data-testid="eta-badge">
                <Clock className="w-3 h-3 mr-1" /> {tracking.eta.eta_minutes} min away
              </Badge>
            )}
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 py-6">
          <Link to="/my-bookings" className="inline-flex items-center gap-2 text-stone-500 hover:text-emerald-950 mb-4 transition-colors text-sm" data-testid="back-link">
            <ArrowLeft className="w-4 h-4" /> Back to My Bookings
          </Link>

          {/* Trip Progress Bar */}
          {!isCompleted && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl border border-stone-100 p-4 mb-4" data-testid="trip-progress">
              <div className="flex items-center justify-between mb-2">
                {TRIP_STEPS.map((step, i) => (
                  <div key={step.key} className="flex items-center flex-1">
                    <div className={`flex items-center gap-2 ${i <= currentStepIdx ? "text-emerald-700" : "text-stone-300"}`}>
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all duration-500 ${
                        i < currentStepIdx ? "bg-emerald-600 border-emerald-600 text-white" :
                        i === currentStepIdx ? "bg-emerald-100 border-emerald-600 text-emerald-700" :
                        "bg-stone-50 border-stone-200 text-stone-400"
                      }`}>
                        {i < currentStepIdx ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
                      </div>
                      <span className={`text-xs font-medium hidden sm:block ${i <= currentStepIdx ? "text-emerald-700" : "text-stone-400"}`}>
                        {step.label}
                      </span>
                    </div>
                    {i < TRIP_STEPS.length - 1 && (
                      <div className={`flex-1 h-0.5 mx-2 rounded transition-all duration-500 ${i < currentStepIdx ? "bg-emerald-500" : "bg-stone-200"}`} />
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Completed Banner */}
          <AnimatePresence>
            {isCompleted && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 mb-4"
                data-testid="completed-banner"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-emerald-600 rounded-full flex items-center justify-center shrink-0">
                    <CheckCircle2 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-emerald-900 text-lg">{firstName} has completed the service</p>
                    <p className="text-sm text-emerald-700">How was your experience?</p>
                  </div>
                </div>
                <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl h-11 gap-2" onClick={() => setShowReview(true)} data-testid="rate-pro-btn">
                  <ThumbsUp className="w-4 h-4" /> Rate {firstName}
                </Button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Arrived Banner */}
          <AnimatePresence>
            {tripStatus === "arrived" && !isCompleted && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                className="bg-blue-50 border border-blue-200 rounded-2xl p-4 mb-4 flex items-center gap-3"
                data-testid="arrived-banner"
              >
                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center shrink-0">
                  <Bell className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-blue-900">{firstName} is waiting outside</p>
                  <p className="text-sm text-blue-700">Your professional has arrived at the pickup location</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Waiting for professional location banner */}
          {!proCoords && !isCompleted && tracking?.professional?.name && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-4 flex items-center gap-3" data-testid="waiting-location-banner">
              <Loader2 className="w-5 h-5 text-amber-600 animate-spin flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-amber-800">Waiting for {proName}'s location...</p>
                <p className="text-xs text-amber-600">Their live location will appear on the map once they start heading your way</p>
              </div>
            </motion.div>
          )}

          {/* Map */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl border border-stone-100 overflow-hidden mb-6" data-testid="tracking-map-container">
            <div style={{ height: "400px" }}>
              <Map
                ref={mapRef}
                {...viewState}
                onMove={(e) => setViewState(e.viewState)}
                mapStyle="mapbox://styles/mapbox/streets-v12"
                mapboxAccessToken={MAPBOX_TOKEN}
                style={{ width: "100%", height: "100%" }}
              >
                <NavigationControl position="top-right" showCompass={false} />

                {proCoords?.length === 2 && (
                  <Marker longitude={proCoords[0]} latitude={proCoords[1]} anchor="center">
                    <div className="relative flex flex-col items-center" data-testid="pro-marker">
                      <div className={`absolute w-14 h-14 rounded-full animate-ping ${tripStatus === "arrived" ? "bg-blue-400/15" : "bg-gray-400/15"}`} />
                      <div className="z-10 drop-shadow-xl" style={{width: 28, height: 44}}>
                        <svg viewBox="0 0 28 44" fill="none" xmlns="http://www.w3.org/2000/svg" style={{width: 28, height: 44}}>
                          {/* Uber-style minimal top-down car */}
                          <rect x="4" y="2" width="20" height="40" rx="9" fill="#111827" />
                          <rect x="6" y="7" width="16" height="7" rx="3" fill="#374151" />
                          <rect x="7" y="29" width="14" height="5" rx="2.5" fill="#374151" />
                          <rect x="1" y="8" width="4" height="8" rx="2" fill="#111827" />
                          <rect x="23" y="8" width="4" height="8" rx="2" fill="#111827" />
                          <rect x="1" y="28" width="4" height="8" rx="2" fill="#111827" />
                          <rect x="23" y="28" width="4" height="8" rx="2" fill="#111827" />
                        </svg>
                      </div>
                      <div className="mt-0.5 px-2 py-0.5 bg-gray-900 text-white text-[10px] font-bold rounded-full shadow-lg z-10 whitespace-nowrap">
                        {proName.split(" ")[0]}
                      </div>
                    </div>
                  </Marker>
                )}

                {!proCoords && pickup?.latitude && (
                  <Marker longitude={pickup.longitude} latitude={pickup.latitude} anchor="center">
                    <div className="relative flex items-center justify-center" data-testid="waiting-marker">
                      <div className="absolute w-16 h-16 rounded-full animate-ping bg-emerald-400/15" />
                      <div className="absolute w-12 h-12 rounded-full animate-pulse bg-emerald-400/20" />
                      <div className="w-8 h-8 rounded-full bg-emerald-600/50 border-2 border-dashed border-emerald-400 flex items-center justify-center">
                        <Loader2 className="w-4 h-4 text-white animate-spin" />
                      </div>
                    </div>
                  </Marker>
                )}

                {pickup?.latitude && (
                  <Marker longitude={pickup.longitude} latitude={pickup.latitude} anchor="bottom">
                    <div className="flex flex-col items-center" data-testid="pickup-marker">
                      <div className="w-8 h-8 bg-blue-600 rounded-full border-2 border-white shadow-lg flex items-center justify-center">
                        <MapPin className="w-4 h-4 text-white" />
                      </div>
                      <div className="mt-1 px-2 py-0.5 bg-blue-600 text-white text-xs font-semibold rounded-full shadow">Pickup</div>
                    </div>
                  </Marker>
                )}

                {dropoff?.latitude && (
                  <Marker longitude={dropoff.longitude} latitude={dropoff.latitude} anchor="bottom">
                    <div className="flex flex-col items-center" data-testid="dropoff-marker">
                      <div className="w-8 h-8 bg-red-600 rounded-full border-2 border-white shadow-lg flex items-center justify-center">
                        <MapPin className="w-4 h-4 text-white" />
                      </div>
                      <div className="mt-1 px-2 py-0.5 bg-red-600 text-white text-xs font-semibold rounded-full shadow">Dropoff</div>
                    </div>
                  </Marker>
                )}

                {routeGeo && (
                  <Source id="route" type="geojson" data={{ type: "Feature", geometry: routeGeo }}>
                    <Layer id="route-line-border" type="line" paint={{ "line-color": "#065f46", "line-width": 6, "line-opacity": 0.3 }} />
                    <Layer id="route-line" type="line" paint={{ "line-color": "#059669", "line-width": 4, "line-opacity": 0.8 }} />
                  </Source>
                )}
              </Map>
            </div>
          </motion.div>

          {/* Professional Info Card */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white rounded-2xl border border-stone-100 p-5 mb-4" data-testid="professional-info-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center overflow-hidden">
                  {tracking?.professional?.picture ? (
                    <img src={tracking.professional.picture} alt="" className="w-12 h-12 rounded-full object-cover" />
                  ) : (
                    <span className="text-emerald-700 text-lg font-bold">{firstName.charAt(0)}</span>
                  )}
                </div>
                <div>
                  <h3 className="font-bold text-emerald-950" data-testid="pro-name">{proName}</h3>
                  <div className="flex items-center gap-2 text-sm text-stone-500">
                    <span>{SERVICE_LABELS[tracking?.request?.service_type] || tracking?.request?.service_type}</span>
                    {tracking?.professional?.avg_rating > 0 && (
                      <span className="flex items-center gap-0.5">
                        <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
                        {tracking.professional.avg_rating.toFixed(1)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <Badge className={`${
                isCompleted ? "bg-stone-100 text-stone-600" :
                tripStatus === "arrived" ? "bg-blue-100 text-blue-700" :
                tripStatus === "arriving" ? "bg-amber-100 text-amber-700" :
                "bg-emerald-100 text-emerald-700"
              }`} data-testid="request-status-badge">
                {isCompleted ? "Completed" : TRIP_STEPS.find(s => s.key === tripStatus)?.label || "En Route"}
              </Badge>
            </div>

            {/* ETA & Distance */}
            {tracking?.eta && !isCompleted && tripStatus !== "arrived" && (
              <div className="grid grid-cols-2 gap-3 mb-4" data-testid="eta-details">
                <div className="bg-emerald-50 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-emerald-700">{tracking.eta.eta_minutes}<span className="text-sm font-normal ml-1">min</span></p>
                  <p className="text-xs text-emerald-600">Estimated arrival</p>
                </div>
                <div className="bg-stone-50 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-stone-700">{tracking.eta.distance_km}<span className="text-sm font-normal ml-1">km</span></p>
                  <p className="text-xs text-stone-500">Distance away</p>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2">
              {tracking?.professional?.phone && (
                <Button variant="outline" className="flex-1" asChild data-testid="call-pro-btn">
                  <a href={`tel:${tracking.professional.phone}`}>
                    <Phone className="w-4 h-4 mr-1.5" /> Call {firstName}
                  </a>
                </Button>
              )}
              <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => navigate(`/chat/${requestId}`)} data-testid="chat-pro-btn">
                <MessageCircle className="w-4 h-4 mr-1.5" /> Chat with {firstName}
              </Button>
            </div>
          </motion.div>

          {/* Trip Details */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white rounded-2xl border border-stone-100 p-5" data-testid="trip-details-card">
            <h4 className="font-semibold text-emerald-950 mb-3">Trip Details</h4>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-3 h-3 rounded-full bg-blue-500 mt-1.5 shrink-0" />
                <div>
                  <p className="text-xs text-stone-400 font-medium">PICKUP</p>
                  <p className="text-sm text-stone-700">{tracking?.request?.pickup_address || "-"}</p>
                </div>
              </div>
              {tracking?.request?.dropoff_address && (
                <div className="flex items-start gap-3">
                  <div className="w-3 h-3 rounded-full bg-red-500 mt-1.5 shrink-0" />
                  <div>
                    <p className="text-xs text-stone-400 font-medium">DROPOFF</p>
                    <p className="text-sm text-stone-700">{tracking.request.dropoff_address}</p>
                  </div>
                </div>
              )}
              {tracking?.location_updated_at && (
                <p className="text-xs text-stone-400 text-right">
                  Location updated: {new Date(tracking.location_updated_at).toLocaleTimeString()}
                </p>
              )}
            </div>
          </motion.div>
        </div>
      </div>
      {showReview && (
        <ReviewModal
          requestId={requestId}
          revieweeName={proName}
          revieweeRole="professional"
          onClose={() => setShowReview(false)}
        />
      )}
    </div>
  );
};

export default LiveTrackingPage;
